import EzFileDragDropController from 'components/util/ez-file-drag-drop/ez-file-drag-drop-controller';
import { Dropzone } from 'dropzone';
import { preventDropzoneFlickerOnDrag } from 'lib/ez-file-drag-drop-helpers';
import { ajaxPost } from 'lib/ajax-helpers';
import { ezErrorDisplay, ezErrorDisplayOff } from 'lib/error-helpers';
import { insertFlashMessage, removeFlashMessage } from 'lib/flash-helpers';
import { isValidImageUrl } from 'lib/helpers';
import { SBI_LOAD_FORM_ERRORS, SBI_CLEAR_DRAG_DROP } from 'lib/ez-custom-events';
import {
  SEARCH_BY_IMAGE_CLICK,
  SEARCH_BY_IMAGE_COMPLETE,
  SEARCH_BY_IMAGE_ERROR,
} from 'components/analytics/conversions/constants/actions';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';

const SBI_LOADING_SPINNER_HTML = '<div class="ez-spinner ez-spinner--color-mono"></div>';

export default class SearchByImageDragDropController extends EzFileDragDropController {
  static values = {
    acceptedFiles: String,
    autoProcessQueue: Boolean,
    disablePreviews: Boolean,
    errorMessagesOverride: Object,
    maxFiles: Number,
    maxFilesize: Number,
    parallelUploads: Number,
    paramName: String,
    successPage: String,
    uploadMultiple: Boolean,
    url: String,
  }

  static targets = [
    'dropContainer',
    'previewsContainer',
    'previewTemplate',
    'urlFieldWrap',
    'urlField',
  ];

  connect() {
    super.connect();

    this.handleDropZone();
    this.handleUrlInput();
    this.setupErrorMessages();

    window.addEventListener(SBI_LOAD_FORM_ERRORS, this.searchByImageFormError);
    window.addEventListener(SBI_CLEAR_DRAG_DROP, this.resetDropContainer);
  }

  disconnect() {
    window.removeEventListener(SBI_LOAD_FORM_ERRORS, this.searchByImageFormError);
    window.removeEventListener(SBI_CLEAR_DRAG_DROP, this.resetDropContainer);

    if (typeof this.urlFieldTarget.removeEventListener === 'function') {
      this.urlFieldTarget.removeEventListener('keyup', this.handleFileUrlEntry);
      this.urlFieldTarget.removeEventListener('paste', this.handleFileUrlEntry);
    }
  }

  searchByImageFormError = () => {
    this.resetErrors();

    this.trackSearchByImage({
      action: SEARCH_BY_IMAGE_ERROR,
      errorMessage: this.genericError,
    });
  }

  handleDropZone = () => {
    if (!this.dropzone) {
      this.dropzone = new Dropzone(this.dropContainerTarget, {

        ...this.dropZoneOptions(),
        ...this.errorMessagesOverrideValue,

        drop: () => {
          this.dropContainerTarget.classList.remove('dz-drag-hover');
          this.resetErrors();
        },
        dragleave: (event) => { // eslint-disable-line no-unused-vars
          preventDropzoneFlickerOnDrag(event, this.dropContainerTarget);
        },
        processing: (file) => { // eslint-disable-line no-unused-vars
          this.handleProcessingSubmission();
        },
        success: (file, response) => {
          this.handleSuccessfulSubmission(response.guid);
        },
        error: (file, error) => {
          if (typeof error !== 'string') error = this.genericError; // eslint-disable-line no-param-reassign

          this.handleUploadError(error);
          this.dropzone.removeFile(file);
        },
      });
    }
  }

  processForm = (event) => {
    event.preventDefault();
  }

  handleUrlInput = () => {
    this.urlField = this.dropContainerTarget.querySelector('[data-search-by-image-drag-drop-target="urlField"]');

    this.urlFieldTarget.addEventListener('paste', (event) => { setTimeout(() => { this.handleFileUrlEntry(event); }, 0); });
    this.urlFieldTarget.addEventListener('keyup', (event) => { this.handleFileUrlEntry(event); });
  }

  handleFileUrlEntry = (event) => {
    if (event.type === 'keyup' && event.key !== 'Enter') return;

    this.handleProcessingSubmission();

    if (isValidImageUrl(this.urlFieldTarget.value)) {
      this.resetErrors();
      this.receiveFileGuid(this.urlFieldTarget.value);
    } else {
      this.handleInvalidUrl();
    }
  }

  handleProcessingSubmission = () => {
    this.trackSearchByImage({ action: SEARCH_BY_IMAGE_CLICK });

    removeFlashMessage(this.dropContainerTarget);

    this.dropContainerTarget.classList.add('file-loading');
    this.dropContainerTarget.insertAdjacentHTML('afterbegin', SBI_LOADING_SPINNER_HTML);
  }

  handleInvalidUrl = () => {
    const errorMessage = this.invalidUrlError;

    const iconCircleInfo2 = `
      <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14.03 14.03">
        <path d="M7,12.71A5.72,5.72,0,1,1,12.72,7,5.72,5.72,0,0,1,7,12.71M7,0a7,7,0,1,0,7,7A7,7,0,0,0,7,0" transform="translate(0.01 0.02)"></path>
        <path id="Fill-3" d="M7,10.83a.65.65,0,0,1-.65-.65V7.06a.65.65,0,0,1,1.3,0v3.12a.65.65,0,0,1-.65.65" transform="translate(0.01 0.02)"></path>
        <path id="Fill-5" d="M7,5.37a1,1,0,1,0-1-1,1,1,0,0,0,1,1" transform="translate(0.01 0.02)"></path>
      </svg>`;

    const invalidMsg = `${iconCircleInfo2}${errorMessage}`;

    this.resetErrors();

    this.trackSearchByImage({ action: SEARCH_BY_IMAGE_ERROR, errorMessage });

    this.resetLoadingStatus();
    ezErrorDisplay(this.urlFieldTarget, invalidMsg);
  }

  resetErrors = () => {
    removeFlashMessage(this.dropContainerTarget);
    ezErrorDisplayOff(this.urlFieldTarget);
  }

  resetLoadingStatus = () => {
    this.dropContainerTarget.querySelectorAll('.ez-spinner').forEach((elem) => elem.parentNode.removeChild(elem));
    this.dropContainerTarget.classList.remove('file-loading');
  }

  setupErrorMessages = () => {
    this.incorrectFileError = this.urlFieldWrapTarget.dataset.searchByImageDragDropIncorrectFileError;
    this.invalidUrlError = this.urlFieldWrapTarget.dataset.searchByImageDragDropInvalidUrlError;
    this.genericError = this.urlFieldWrapTarget.dataset.searchByImageDragDropGenericError;
  }

  handleUploadError = (flashMessageText) => {
    this.resetErrors();

    this.trackSearchByImage({
      action: SEARCH_BY_IMAGE_ERROR,
      errorMessage: flashMessageText,
    });

    insertFlashMessage({ referenceElement: this.dropContainerTarget, flashMessageText });
  }

  receiveFileGuid = (imageUrl) => {
    ajaxPost(`/api/v2/search_images?search_image[url]=${imageUrl}`,
      async (response) => {
        if (response.status === 500) {
          this.handleUploadError(this.genericError);
          return;
        }

        if (response.data) {
          this.handleSuccessfulSubmission(response.data.guid);
        } else {
          this.handleUploadError(this.incorrectFileError);
        }
      },
      () => {
        this.handleUploadError(this.incorrectFileError);
      });
  }

  handleSuccessfulSubmission = (guid) => {
    this.trackSearchByImage({ action: SEARCH_BY_IMAGE_COMPLETE });

    removeFlashMessage(this.dropContainerTarget);
    this.resetLoadingStatus();

    this.dropContainerTarget.classList.add('file-success');

    document.querySelector('.search-by-image__requirements')?.classList.add('is-hidden');

    Turbolinks.visit(`/search_by_image/${guid}?content_type=image`);
  }

  resetDropContainer = () => {
    removeFlashMessage(this.dropContainerTarget);
    this.urlField.value = '';
  }

  trackSearchByImage({ action, errorMessage = null }) {
    const label = errorMessage ? `${this.selectedContentType} - ${errorMessage}` : `Content-${this.selectedContentType}`;

    sendTrackingEvent({
      data: {
        action: `${action}`,
        label,
      },
    });
  }

  get selectedContentType() {
    if (!document.querySelector('.ez-search-form')) return 'Images';

    return document.querySelector('.ez-search-form [data-search-form-target="input"]').dataset.contentType;
  }
}
